import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Eye, Inbox, Undo2 } from 'lucide-react';
import { StatusBadge } from '../../common/StatusBadge';
import { useOwnerService } from '../../../contexts/OwnerServiceContext';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { brtaBadge, can, isBlockingDuplicate, queueMeta, queueOf, typeLabel } from '../../../utils/applicationQueue';
import { toBn } from '../../../utils/bn';
import type { OwnerApplication } from '../../../types/owner';
interface Props {
  rows: OwnerApplication[];
  officerOf: (a: OwnerApplication) => string | undefined;
  onReturn: (a: OwnerApplication) => void;
}
export function ApplicationTable({
  rows,
  officerOf,
  onReturn
}: Props) {
  const navigate = useNavigate();
  const {
    applications
  } = useOwnerService();
  const {
    role
  } = useApplicationReview();
  const open = (a: OwnerApplication, tab?: string) => navigate(`/admin/owner/applications/${a.id}${tab ? `?tab=${tab}` : ''}`);
  if (!rows.length) {
    return <div className="flex flex-col items-center justify-center py-16 text-center text-sm text-slate-500" data-id="element-168" data-mp="opjfxjsnonqn">
        <Inbox className="mb-2 h-10 w-10 text-slate-300" data-id="element-169" data-mp="12p2naadn7v1w" />
        এই ফিল্টারে কোনো আবেদন পাওয়া যায়নি
      </div>;
  }
  return <div className="overflow-x-auto" data-id="element-170" data-mp="ynz5vjfy5qyk">
      <table className="w-full min-w-[1100px] text-sm" data-id="element-171" data-mp="axdaosr1k15z">
        <thead data-id="element-172" data-mp="axv47hmbk1a7">
          <tr className="border-b border-border bg-slate-50 text-left text-xs font-semibold text-slate-500" data-id="element-173" data-mp="avil314trkvk">
            {['ক্রমিক', 'আবেদন নম্বর', 'আবেদনের তারিখ', 'মালিকের নাম', 'গাড়ির নম্বর', 'গাড়ির ধরন', 'BRTA যাচাই', 'আবেদন অবস্থা', 'দায়িত্বপ্রাপ্ত কর্মকর্তা', 'অ্যাকশন'].map(h => <th key={h} scope="col" className="whitespace-nowrap px-3 py-2.5" data-id="element-174" data-mp="ex3xkldg5x58">{h}</th>)}
          </tr>
        </thead>
        <tbody data-id="element-175" data-mp="3jdonu5by5svh">
          {rows.map((a, i) => {
          const q = queueOf(a);
          const brta = brtaBadge(a);
          const dup = isBlockingDuplicate(a, applications);
          const final = q === 'approved' || q === 'rejected';
          return <tr key={a.id} onClick={() => open(a)} className="cursor-pointer border-b border-slate-100 transition hover:bg-navy-50/40" data-id="element-176" data-mp="1r9iv3sjz6d4g" data-mpi={i}>
                <td className="px-3 py-2.5 text-slate-500" data-id="element-177" data-mp="211yd2pv9v1pc">{toBn(i + 1)}</td>
                <td className="px-3 py-2.5" data-id="element-178" data-mp="13e6f3toe3wgp">
                  <Link to={`/admin/owner/applications/${a.id}`} onClick={e => e.stopPropagation()} className="font-mono text-[12.5px] font-semibold text-navy-800 hover:underline" data-id="element-179" data-mp="13f80os3181co">{a.id}</Link>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-600" data-id="element-180" data-mp="1dasxmbt8ubfk">{a.submitted}</td>
                <td className="whitespace-nowrap px-3 py-2.5 font-medium text-navy-900" data-id="element-181" data-mp="1ics07jh5t4bg">{a.ownerName}</td>
                <td className="whitespace-nowrap px-3 py-2.5" data-id="element-182" data-mp="2oqztp0e9dj5p">
                  <span className="font-medium text-navy-900" data-id="element-183" data-mp="2tpb0t3d65fk8">{a.vehicleReg}</span>
                  {dup && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-red-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-red-700" title="ডুপ্লিকেট রেজিস্ট্রেশন" data-id="element-184" data-mp="i3a7vw6amh6r"><AlertTriangle className="h-3 w-3" data-id="element-185" data-mp="d6a2zol6jkh8" />ডুপ্লিকেট</span>}
                </td>
                <td className="px-3 py-2.5 text-slate-700" data-id="element-186" data-mp="8b4kz0uwepyf">{typeLabel(a)}</td>
                <td className="px-3 py-2.5" data-id="element-187" data-mp="3dl2tpv2xt17"><StatusBadge tone={brta.tone} data-id="element-188" data-mp="3c9zfr5jqnam">{brta.label}</StatusBadge></td>
                <td className="px-3 py-2.5" data-id="element-189" data-mp="3ulqlaqc5k3rv"><StatusBadge tone={queueMeta[q].tone} data-id="element-190" data-mp="3qity0mbo3jhl">{queueMeta[q].label}</StatusBadge></td>
                <td className="whitespace-nowrap px-3 py-2.5 text-slate-600" data-id="element-191" data-mp="3ql6ndkjr8w4o">{officerOf(a) ?? <span className="text-slate-400" data-id="element-192" data-mp="3ljzibt8zyb1p">নির্ধারিত হয়নি</span>}</td>
                <td className="px-3 py-2" onClick={e => e.stopPropagation()} data-id="element-193" data-mp="3glxyh63qy7p6">
                  <div className="flex flex-wrap gap-1" data-id="element-194" data-mp="1jsmdf471435n">
                    <RowBtn label="বিস্তারিত দেখুন" icon={Eye} onClick={() => open(a)} data-id="element-195" data-mp="1jq07ta3p7xq2" />
                    {!final && <RowBtn label="যাচাই করুন" icon={ClipboardCheck} onClick={() => open(a, 'validation')} tone="navy" data-id="element-196" data-mp="1ergjx45jobv2" />}
                    {q === 'awaiting' && can(role, 'approve') && <RowBtn label="অনুমোদন করুন" icon={CheckCircle2} onClick={() => open(a, 'validation')} tone="green" data-id="element-197" data-mp="ggx47nk5brgg" />}
                    {!final && q !== 'returned' && can(role, 'return') && <RowBtn label="সংশোধনের জন্য ফেরত" icon={Undo2} onClick={() => onReturn(a)} tone="amber" data-id="element-198" data-mp="7fz7zrfy8o8f" />}
                  </div>
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
const tones = {
  gray: 'border-slate-200 text-slate-700 hover:bg-slate-100',
  navy: 'border-navy-200 text-navy-800 hover:bg-navy-50',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100',
  amber: 'border-amber-200 text-amber-800 hover:bg-amber-50'
};
function RowBtn({
  label,
  icon: Icon,
  onClick,
  tone = 'gray'
}: {
  label: string;
  icon: typeof Eye;
  onClick: () => void;
  tone?: keyof typeof tones;
}) {
  return <button onClick={onClick} title={label} className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-2 py-1 text-[11.5px] font-medium ${tones[tone]}`} data-id="element-199" data-mp="2rb5xn82vh9m9">
      <Icon className="h-3.5 w-3.5" data-id="element-200" data-mp="319vfx1yokw31" />
      {label}
    </button>;
}